import React, { Context, createContext, useCallback, useContext } from 'react';
import { AppContext } from '../schema/AppContext';
import { useNewAppContextRef } from '../hooks/appContextHelperHooks';
import {
    ReactAppContextProviderProps,
    ReactAppContextRefType,
} from '../schema/ReactAppContextProvider.types';

export const ReactAppCtx: Context<any> = createContext<any>(undefined);

export const useReactAppCtx = (): any => useContext(ReactAppCtx);

// Deprecate the above two once we move mini to new context and workflows
const ReactAppContext = createContext<ReactAppContextRefType | null>(null);

const ReactAppContextDispatchContext = createContext<null | ((newContext: AppContext) => void)>(
    null
);
const ReactAppContextResetContext = createContext<null | (() => void)>(null);

export const useReactAppContextRef = (): ReactAppContextRefType => {
    const reactAppCtxRef = useContext(ReactAppContext);
    if (!reactAppCtxRef) {
        throw new Error('invoke useReactAppContext inside ReactAppContextProvider');
    } else if (!reactAppCtxRef.current) {
        throw new Error('reactAppContext is invalid');
    }

    return reactAppCtxRef;
};

export const useReactAppContext = (): AppContext => {
    const reactAppCtxRef = useReactAppContextRef();

    return reactAppCtxRef.current;
};

export const useReactAppContextDispatchContext = (): ((newContext: AppContext) => void) => {
    const dispatch = useContext(ReactAppContextDispatchContext);
    if (!dispatch) {
        throw new Error('invoke useReactAppContextDispatchContext inside ReactAppContextProvider');
    }
    return dispatch;
};

export const useReactAppContextResetContext = (): (() => void) => {
    const reset = useContext(ReactAppContextResetContext);
    if (!reset) {
        throw new Error('invoke useReactAppContextResetContext inside ReactAppContextProvider');
    }
    return reset;
};

export function ReactAppContextProvider<T>({
    children,
    customRef,
    customizations = {},
}: ReactAppContextProviderProps<T>): JSX.Element {
    const [appCtxRef, resetAppCtxRef] = useNewAppContextRef<T>(customizations, customRef);

    const changeContext = useCallback(
        (newAppCtx: AppContext) => {
            appCtxRef.current = newAppCtx ?? appCtxRef.current;
        },
        [appCtxRef]
    );
    return (
        <ReactAppContextResetContext.Provider value={resetAppCtxRef}>
            <ReactAppContext.Provider value={appCtxRef}>
                <ReactAppContextDispatchContext.Provider value={changeContext}>
                    {children}
                </ReactAppContextDispatchContext.Provider>
            </ReactAppContext.Provider>
        </ReactAppContextResetContext.Provider>
    );
}
